import { Collapsible, CollapsibleContent, CollapsibleTrigger } from 'components/ui/collapsible';
import { useState } from 'react';
import { Button } from 'components/ui/button';
import { ChevronDown, Loader2, RotateCcw, Search } from 'lucide-react';
import { Checkbox } from 'components/ui/checkbox';
import { Input } from 'components/ui/input';
import { Typography } from 'components/ui/typography';
import { cn } from 'lib/utils';
import {
  Currency,
  Marketplace,
  StoreIntegrationType,
  StoresFiltersInput,
} from 'app/graphql/generated/admin/graphql';
import { useAppSelector } from 'app/store';
import { selectUserInfo, selectUserInfoLoading } from 'app/store/user';
import { useQuery } from '@apollo/client';
import { STORES, TOTAL_STORES } from 'app/graphql';
import InfiniteScroll from 'components/ui/infinite-scroll';
import { useDebouncedCallback } from 'use-debounce';
import { createFallbackImageUrl, formatNumberShort } from 'pages/Catalog/utils';
import * as Sentry from '@sentry/react';

type FacetsProps = {
  onFacetChange: (value: FacetChangeValue) => void;
  storeDomains: string[];
  currencies: Currency[];
  onReset?: (value: { type: 'store' | 'currency' }) => void;
  showReset: {
    store: boolean;
    currency: boolean;
  };
};

type FacetGroupProps = {
  title: string;
  count?: string;
  isOpen?: boolean;
  children: React.ReactNode;
  showReset?: boolean;
  onReset?: () => void;
};

type FacetStoreSelectedValue = {
  type: 'store';
  value: string;
  isChecked: boolean;
};

type FacetCurrencySelectedValue = {
  type: 'currency';
  value: Currency;
  isChecked: boolean;
};

type FacetPriceValue = {
  type: 'price';
  minPrice?: number;
  maxPrice?: number;
};

export type FacetChangeValue =
  | FacetStoreSelectedValue
  | FacetCurrencySelectedValue
  | FacetPriceValue;

type StoreFacetSelectionProps = {
  label: string;
  value: string;
  logoUrl?: string;
  marketplace: Marketplace;
  domain: string;
  isChecked: boolean;
  onCheckedChange: (value: FacetStoreSelectedValue) => void;
};

type CurrencyFacetSelectionProps = {
  label: string;
  value: Currency;
  isChecked: boolean;
  disabled?: boolean;
  onCheckedChange: (value: FacetCurrencySelectedValue) => void;
};

const initialStoreInputs: { filters: StoresFiltersInput; pagination: { first: number } } = {
  filters: {
    integrationType: StoreIntegrationType.Integrated,
    isLive: true,
    marketplace: Marketplace.Shopify,
  },
  pagination: {
    first: 10,
  },
};

const StoreLogo = ({
  logoUrl,
  domain,
  marketplace,
  label,
}: {
  logoUrl?: string;
  domain: string;
  marketplace: Marketplace;
  label: string;
}) => {
  const [errored, setErrored] = useState(false);
  const initials = label
    .split(/\s+/)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase() ?? '')
    .join('');
  const src = logoUrl || createFallbackImageUrl(domain, marketplace);
  return (
    <Typography
      variant="mono"
      as="span"
      className="inline-grid h-[22px] w-[22px] flex-shrink-0 place-items-center overflow-hidden border border-[var(--line)] bg-[var(--surface-2)] text-[9px] font-semibold text-[var(--text-muted)]"
    >
      {!errored && src ? (
        <img
          src={src}
          alt=""
          className="h-full w-full object-contain"
          onError={() => setErrored(true)}
        />
      ) : (
        initials
      )}
    </Typography>
  );
};

const StoreFacetItem = ({
  label,
  value,
  isChecked,
  onCheckedChange,
  logoUrl,
  domain,
  marketplace,
}: StoreFacetSelectionProps) => {
  const handleCheckedChange = (checked: boolean) => {
    onCheckedChange({ type: 'store', value, isChecked: checked });
  };

  return (
    <Typography variant="small" asChild>
      <label
        htmlFor={value}
        className="flex cursor-pointer items-center gap-2.5 text-[var(--text)]"
      >
        <Checkbox
          id={value}
          value={value}
          onCheckedChange={handleCheckedChange}
          checked={isChecked}
        />
        <StoreLogo logoUrl={logoUrl} domain={domain} marketplace={marketplace} label={label} />
        <span className="truncate leading-none">{label}</span>
      </label>
    </Typography>
  );
};

const CurrencyFacetItem = ({
  label,
  value,
  isChecked,
  onCheckedChange,
  disabled,
}: CurrencyFacetSelectionProps) => {
  const handleCheckedChange = (checked: boolean) => {
    onCheckedChange({ type: 'currency', value, isChecked: checked });
  };

  return (
    <Typography variant="small" asChild>
      <label
        htmlFor={value}
        className="flex cursor-pointer items-center gap-2.5 text-[var(--text)]"
      >
        <Checkbox
          id={value}
          value={value}
          onCheckedChange={handleCheckedChange}
          checked={isChecked}
          disabled={disabled}
        />
        <span className="leading-none">{label}</span>
      </label>
    </Typography>
  );
};

const FacetGroup = ({
  title,
  count,
  isOpen = false,
  children,
  showReset,
  onReset,
}: FacetGroupProps) => {
  const [isOpenState, setIsOpen] = useState(isOpen);

  return (
    <Collapsible
      open={isOpenState}
      onOpenChange={setIsOpen}
      className="border-b border-[var(--line)]"
    >
      {/* Whole head row is the toggle. Reset action sits inside but
          stops propagation so clicking the icon doesn't also collapse. */}
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full cursor-pointer items-center justify-between gap-2 px-4 py-3.5 text-left transition-colors hover:bg-[var(--surface-2)] focus:outline-none focus-visible:bg-[var(--surface-2)]"
        >
          <Typography
            variant="eyebrow"
            as="span"
            className="flex items-baseline gap-1.5 text-[var(--text)]"
          >
            <span>{title}</span>
            {count && (
              <Typography
                variant="small"
                as="span"
                className="text-[10px] normal-case tracking-normal"
              >
                ({count})
              </Typography>
            )}
          </Typography>
          <span className="flex items-center gap-1 text-[var(--text-muted)]">
            {showReset && (
              <span
                role="button"
                tabIndex={0}
                aria-label="Reset"
                onClick={(e) => {
                  // Don't let the reset toggle the collapsible.
                  e.stopPropagation();
                  onReset?.();
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    e.stopPropagation();
                    onReset?.();
                  }
                }}
                className="inline-flex h-6 w-6 items-center justify-center text-[var(--text-muted)] transition-colors hover:text-[var(--text)] focus:outline-none focus-visible:text-[var(--text)]"
              >
                <RotateCcw className="h-3.5 w-3.5" />
              </span>
            )}
            <ChevronDown
              className={cn(
                'h-3.5 w-3.5 transition-transform duration-200',
                isOpenState ? 'rotate-180' : 'rotate-0',
              )}
              aria-hidden="true"
            />
          </span>
        </button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <div className="space-y-2.5 px-4 pb-4">{children}</div>
      </CollapsibleContent>
    </Collapsible>
  );
};

export const Facets = ({
  onFacetChange,
  storeDomains,
  currencies,
  onReset,
  showReset,
}: FacetsProps) => {
  const [minPrice, setMinPrice] = useState<string>('');
  const [maxPrice, setMaxPrice] = useState<string>('');
  const [isSearching, setIsSearching] = useState(false);
  const [currencyList, setCurrencyList] = useState<Currency[]>(Object.values(Currency));

  const { adminToken } = useAppSelector(selectUserInfo) || {};
  const loadingInfo = useAppSelector(selectUserInfoLoading);

  const {
    fetchMore,
    loading: queryLoading,
    refetch,
    data,
  } = useQuery(STORES, {
    context: {
      clientName: 'admin',
    },
    skip: !adminToken || loadingInfo,
    variables: initialStoreInputs,
    onError: (error) => {
      Sentry.captureException(new Error('Failed to fetch stores', { cause: error }));
    },
  });

  const { data: totalStoresData } = useQuery(TOTAL_STORES, {
    context: {
      clientName: 'admin',
    },
    skip: !adminToken || loadingInfo,
    variables: initialStoreInputs,
    onError: (error) => {
      Sentry.captureException(new Error('Failed to fetch total stores', { cause: error }));
    },
  });

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const stores: any[] = data?.stores.edges.map((edge: any) => edge.node) ?? [];
  const totalStores = formatNumberShort(totalStoresData?.totalStores.count ?? 0);
  const hasMore = data?.stores.pageInfo.hasNextPage ?? false;
  const endCursor = data?.stores.pageInfo.endCursor?.valueOf();

  const handleSearch = useDebouncedCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    setIsSearching(true);
    const value = e.target.value;
    if (value.length === 0) {
      await refetch(initialStoreInputs);
    } else {
      await refetch({
        ...initialStoreInputs,
        filters: {
          ...initialStoreInputs.filters,
          searchText: value,
        },
      });
    }
    setIsSearching(false);
  }, 300);

  const next = async () => {
    await fetchMore({
      variables: {
        pagination: {
          after: endCursor,
          first: 10,
        },
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult) {
          return prev;
        }

        return {
          stores: {
            ...fetchMoreResult.stores,
            edges: [...prev.stores.edges, ...fetchMoreResult.stores.edges],
            pageInfo: fetchMoreResult.stores.pageInfo,
          },
        };
      },
    });
  };

  const handlePriceFilter = () => {
    if (minPrice.length === 0 && maxPrice.length === 0) {
      return;
    }

    onFacetChange({
      type: 'price',
      minPrice: minPrice.length ? Number(minPrice) * 100 : undefined,
      maxPrice: maxPrice.length ? Number(maxPrice) * 100 : undefined,
    });
  };

  const handleSearchCurrencies = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (value.length === 0) {
      setCurrencyList(Object.values(Currency));
    } else {
      setCurrencyList(
        Object.values(Currency).filter((currency) =>
          currency.toLowerCase().includes(value.toLowerCase()),
        ),
      );
    }
  };

  const currenciesSelected =
    currencies.length < 4 ? currencies.join(', ') : `${currencies.length - 1}+ selected`;

  const minPriceValue = minPrice.length
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencies[0],
      }).format(Number(minPrice))
    : '0';
  const maxPriceValue = maxPrice.length
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: currencies[0],
      }).format(Number(maxPrice))
    : '∞';

  const priceSubtitle = `${minPriceValue} – ${maxPriceValue}`;

  const handleClearPriceFilter = () => {
    setMinPrice('');
    setMaxPrice('');
    onFacetChange({
      type: 'price',
      minPrice: undefined,
      maxPrice: undefined,
    });
  };

  return (
    <div>
      <FacetGroup
        title="Stores"
        isOpen={true}
        count={`${totalStores} stores`}
        showReset={showReset.store}
        onReset={() => onReset?.({ type: 'store' })}
      >
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]"
            aria-hidden="true"
          />
          <Input placeholder="Search stores…" className="h-8 w-full pl-7" onChange={handleSearch} />
          {isSearching && (
            <Loader2 className="absolute right-2 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-[var(--text-muted)]" />
          )}
        </div>
        <div className="flex max-h-[280px] flex-col gap-2.5 overflow-y-auto">
          {stores.map((store) => (
            <StoreFacetItem
              key={store.id}
              label={store.name}
              value={store.domain}
              logoUrl={store.logoUrl}
              marketplace={store.marketplace}
              domain={store.domain}
              isChecked={storeDomains.includes(store.domain)}
              onCheckedChange={onFacetChange}
            />
          ))}
          {stores.length === 0 && !queryLoading && (
            <Typography variant="small" as="div">
              No stores found
            </Typography>
          )}
          <InfiniteScroll hasMore={hasMore} isLoading={queryLoading} next={next} threshold={1}>
            {hasMore && <Loader2 className="my-2 h-4 w-4 animate-spin text-[var(--text-muted)]" />}
          </InfiniteScroll>
        </div>
      </FacetGroup>

      <FacetGroup
        title="Currency"
        count={currenciesSelected}
        showReset={showReset.currency}
        onReset={() => onReset?.({ type: 'currency' })}
      >
        <div className="relative">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-[var(--text-muted)]"
            aria-hidden="true"
          />
          <Input
            placeholder="Search currencies…"
            className="h-8 w-full pl-7"
            onChange={handleSearchCurrencies}
          />
        </div>
        <div className="flex max-h-[240px] flex-col gap-2.5 overflow-y-auto">
          {currencyList.map((currency) => (
            <CurrencyFacetItem
              key={currency}
              label={currency}
              value={currency}
              isChecked={currencies.includes(currency)}
              onCheckedChange={onFacetChange}
              disabled={maxPrice.length > 0 || minPrice.length > 0}
            />
          ))}
        </div>
      </FacetGroup>

      <FacetGroup
        title="Price"
        isOpen={minPrice.length > 0 || maxPrice.length > 0}
        count={priceSubtitle}
      >
        <div className="flex flex-col gap-2">
          <div className="flex gap-2">
            <Input
              placeholder="Min"
              type="number"
              className="h-8"
              value={minPrice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMinPrice(e.target.value)}
            />
            <Input
              placeholder="Max"
              type="number"
              className="h-8"
              value={maxPrice}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMaxPrice(e.target.value)}
            />
          </div>
          <div className="flex gap-2">
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={handlePriceFilter}
              disabled={(minPrice.length === 0 && maxPrice.length === 0) || currencies.length !== 1}
              className="flex-1"
            >
              Apply
            </Button>
            <Button type="button" variant="outline" size="sm" onClick={handleClearPriceFilter}>
              Clear
            </Button>
          </div>
        </div>
      </FacetGroup>
    </div>
  );
};
